import React from 'react';
import { Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { isDemoBusinessId } from '@/lib/demo/demoBusiness';
import { getPublicMenuPath } from '@/lib/tenant/getPublicMenuPath';
import { canShowPublicAssistant } from '@/lib/menuPlanCapabilities';

/**
 * @returns {React.ReactNode | null} Pantalla completa de carga/error, o null si el chat puede renderizarse.
 */
export function renderAssistantPageState({
  businessIdReady,
  routeBid,
  businessId,
  loading,
  loadError,
  menuData,
  onRetry,
  navigate,
}) {
  if (!businessIdReady && !routeBid) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Bot className="w-12 h-12 animate-pulse text-primary" />
      </div>
    );
  }

  if (businessIdReady && !businessId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-background to-secondary/20">
        <Card className="p-10 text-center max-w-md space-y-3">
          <h2 className="text-xl font-semibold">No pudimos encontrar este negocio</h2>
          <p className="text-sm text-muted-foreground">
            Ábrelo desde el enlace del negocio o revisa que el dominio esté bien configurado.
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    if (isDemoBusinessId(businessId)) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-violet-950/20 to-background flex flex-col items-center justify-center px-6 gap-6">
          <div className="flex items-center gap-3">
            <Bot className="w-10 h-10 text-violet-600 animate-pulse" />
            <div className="space-y-2">
              <div className="h-4 w-48 rounded bg-muted animate-pulse" />
              <div className="h-3 w-36 rounded bg-muted/80 animate-pulse" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center max-w-xs">
            Preparando asistente demo…
          </p>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Bot className="w-12 h-12 animate-pulse text-primary" />
      </div>
    );
  }

  if (loadError && !menuData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 bg-gradient-to-b from-background to-secondary/20">
        <p className="text-sm text-muted-foreground text-center max-w-sm">
          No se pudo cargar el asistente. Reintentá o volvé al menú.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button type="button" onClick={onRetry}>
            Reintentar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(getPublicMenuPath(routeBid, 'home'))}
          >
            Volver al menú
          </Button>
        </div>
      </div>
    );
  }

  if (menuData && !canShowPublicAssistant(menuData.business)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 bg-gradient-to-b from-background to-secondary/20">
        <Card className="p-8 text-center max-w-md space-y-3">
          <h2 className="text-xl font-semibold">Asistente no disponible</h2>
          <p className="text-sm text-muted-foreground">
            Este local no tiene el asistente activo en su plan actual. Podés pedir desde la carta.
          </p>
          <Button type="button" onClick={() => navigate(getPublicMenuPath(routeBid, 'catalog'))}>
            Ver carta
          </Button>
        </Card>
      </div>
    );
  }

  return null;
}
